import { Cancel01Icon } from '@hugeicons/core-free-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  Button,
  ButtonVariants,
  Colors,
  IconButton,
  isWeb,
  TextBold,
  TextReg,
  useAuthStore,
  useMediaQueries,
} from '@sunrise-ui/primitives';
import React, { useEffect, useState } from 'react';
import { Linking, StyleSheet, View } from 'react-native';
import { APP_STORE_APPLE_URL, APP_STORE_GOOGLE_URL } from '../../utils/env';
import { AppDownloadQRModal } from './AppDownloadQRModal';

type Props = {
  onDismiss?: () => void;
};

const APP_STORE_LINKS = {
  ios: APP_STORE_APPLE_URL,
  android: APP_STORE_GOOGLE_URL,
};

export function AppDownloadBanner({ onDismiss }: Props) {
  const { isMobile } = useMediaQueries();
  const { currentUserId, isAuthenticated } = useAuthStore();
  const [showQRModal, setShowQRModal] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Create user-specific storage key (follows existing app patterns)
  const getBannerDismissKey = () => {
    const userId = isAuthenticated ? currentUserId : 'anonymous';
    return `@hasSeenAppBanner_${userId}`;
  };

  // Check if we should show the banner
  useEffect(() => {
    const checkBannerState = async () => {
      // Don't show if this is a native app
      if (!isWeb) {
        setIsDismissed(true);
        return;
      }

      // Don't show on iOS Safari since it has native Smart App Banner
      const isIOSSafari =
        /iPad|iPhone|iPod/.test(navigator.userAgent) &&
        /Safari/.test(navigator.userAgent) &&
        !/Chrome|CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);

      if (isIOSSafari) {
        setIsDismissed(true);
        return;
      }

      try {
        // Check if banner was previously dismissed
        const dismissed = await AsyncStorage.getItem(getBannerDismissKey());
        if (dismissed === 'true') {
          setIsDismissed(true);
        }
      } catch (error) {
        console.warn('Failed to check banner dismiss state:', error);
      }
    };

    void checkBannerState();
  }, [currentUserId, isAuthenticated]);

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
    void AsyncStorage.setItem(getBannerDismissKey(), 'true');
  };

  const handleDownloadClick = () => {
    if (isMobile) {
      // On mobile web, try to detect platform and redirect directly
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);

      if (isIOS) {
        void Linking.openURL(APP_STORE_LINKS.ios);
      } else if (isAndroid) {
        void Linking.openURL(APP_STORE_LINKS.android);
      } else {
        // Fallback: show both options
        setShowQRModal(true);
      }
    } else {
      // On desktop, show QR code modal
      setShowQRModal(true);
    }
  };

  if (isDismissed || !isWeb) {
    return null;
  }

  return (
    <>
      <View style={styles.bannerContainer}>
        <View style={styles.banner}>
          <View style={styles.content}>
            <View style={styles.textContainer}>
              <TextBold style={styles.title}>We have an app!</TextBold>
              <TextReg style={styles.description}>
                {isMobile
                  ? 'Download to see all events in MSP'
                  : 'Scan QR code to download on your phone'}
              </TextReg>
            </View>

            <View style={styles.actions}>
              <Button
                mode="contained"
                size="small"
                variant={ButtonVariants.PURPLE}
                onPress={handleDownloadClick}
                contentStyle={styles.downloadButtonContent}
                compact
              >
                {isMobile ? 'Open' : 'Get App'}
              </Button>

              <IconButton
                icon={Cancel01Icon}
                onPress={handleDismiss}
                style={styles.dismissButton}
              />
            </View>
          </View>
        </View>
      </View>

      {/* QR Modal for desktop users */}
      <AppDownloadQRModal
        visible={showQRModal}
        hide={() => setShowQRModal(false)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  bannerContainer: {
    backgroundColor: Colors.purple0,
  },
  banner: {
    backgroundColor: Colors.opacity05,
    borderBottomWidth: 1,
    borderBottomColor: Colors.opacity10,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  textContainer: {
    flex: 1,
    marginRight: 12,
  },
  title: {
    color: Colors.tint,
    fontSize: 14,
    marginBottom: 2,
    fontWeight: 'bold',
  },
  description: {
    color: Colors.text,
    fontSize: 12,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  downloadButtonContent: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  dismissButton: {
    backgroundColor: 'transparent',
  },
});
